<template>
  <v-card tile class="vh-100">
    <v-system-bar color="darken-2" class="pr-1 height-50">
      <h3>
        <v-btn icon :to="{ name: 'timeclock' }" class="transparent">
          <v-icon color="grey lighten-1" class="">mdi-chevron-left</v-icon>
        </v-btn>
        <span v-if="timeClock && timeClock.clockStart && timeClockId">
          <span v-if="timeClockEndDate && timeClockStartDate != timeClockEndDate">
            {{ formatDateMonthDay(timeClock.clockStart) }} -
            {{ formattedEndDate }}
          </span>
          <span v-else>
            {{ formatDateFull(timeClock.clockStart) }}
          </span>
        </span>
      </h3>
      <v-spacer></v-spacer>
      <v-switch
        small
        v-if="!timeClockId"
        v-model="useLastClock"
        @change="fillLastClock"
        :label="$t('v_timeclockdetail.t_quick_fill')"
        class="mr-4"
      >
      </v-switch>
    </v-system-bar>

    <v-alert
      v-if="!$online"
      dense
      tile
      color="gray"
      icon="mdi-alert-circle-outline"
      class="alert-offline text-headline"
    >
      {{ $t("t_no_connection") }}
    </v-alert>

    <v-card-text class="pt-0 pb-10 mt-6">
      <v-form ref="formTimeClock" v-model="isValid">
        <!-- replace with new time selector -->
        <div class="ma-4" v-if="timeClockId">
          <v-row class="featured rounded" v-if="!showTimeEdit && timeClock && timeClock.clockStart">
            <v-col sm="8">
              <h3>
                {{ formatDate(timeClock.clockStart) }}
                <span v-if="formatDate(timeClock.clockStart) !== formatDate(timeClock.clockEnd) && timeClock.clockEnd">
                  - {{ formatDate(timeClock.clockEnd) }}</span
                >
              </h3>
              <h4>
                {{ formatTime(timeClock.clockStart) }} -
                {{ timeClock.clockEnd ? formatTime(timeClock.clockEnd) : $t("t_clocked_in") }}
              </h4>
            </v-col>
            <v-col :sm="!timeClock.clockEnd ? '4' : '3'" class="text-right mt-3">
              <h2 v-if="!timeClock.clockEnd">
                {{ timeClockDuration }}
              </h2>
              <h2 v-else>
                {{ formatDuration(timeClock.clockStart, timeClock.clockEnd) }}
              </h2>
            </v-col>
            <v-col sm="1" class="text-right mt-1" v-if="timeClock.clockEnd">
              <v-btn icon @click="showTimeEdit = !showTimeEdit">
                <v-icon>mdi-square-edit-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <!-- /Overview TimeClock -->

        <!-- DateTime edit -->
        <div v-if="showTimeEdit" class="featured rounded px-2">
          <v-row>
            <v-col cols="5">
              <v-text-field
                :label="$t('v_timeclockdetail.t_duration')"
                placeholder="00:00"
                autocomplete="off"
                v-model="timeClockNewDuration"
                :rules="[(v) => !!v || $t('t_val_req_time')]"
                required
                color="primary"
                style="font-size: 24px"
                class="centered-input"
                onclick="this.select()"
                hide-details
                @change="
                  changeTimeClockDuration($event);
                  timeInputsTouched.duration = true;
                "
              >
                <v-icon slot="prepend"> mdi-timer-sand </v-icon></v-text-field
              >
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-0">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedStartDate"
                    :label="$t('v_timeclockdetail.t_start_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    :hide-details="true"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="timeClockStartDate"
                  @input="
                    menuStartDate = false;
                    changeTime();
                  "
                  :locale="locale"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="1" class="text-center pt-0 px-0 max-width-30">
              <v-btn
                icon
                small
                @click="resetEndDate"
                class="mt-3"
                v-if="(timeClockEndDate && timeClockStartDate != timeClockEndDate) || showEndDate"
              >
                <v-icon>mdi-minus-circle</v-icon>
              </v-btn>
              <v-btn icon small @click="toggleEndDate" class="mt-3" color="primary" v-else>
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </v-col>
            <v-col
              cols="5"
              class="pt-0"
              v-if="(timeClockEndDate && timeClockStartDate != timeClockEndDate) || showEndDate"
            >
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedEndDate"
                    :label="$t('v_timeclockdetail.t_end_date')"
                    readonly
                    :hide-details="true"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="timeClockEndDate"
                  @input="
                    menuEndDate = false;
                    changeTime();
                  "
                  :locale="locale"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <time-select
                v-model="timeClockStartTime"
                :label="$t('v_timeclockdetail.t_start_time')"
                :rules="[(v) => !!v || $t('t_val_req_time')]"
                :times="timeOptions"
                :match-value="timeClockEndTime"
                :original-datetime="timeClock?.clockStart"
                @update:datetime="updateStartDateTime"
                @input="
                  timeInputsTouched.startTime = true;
                  changeTime();
                "
              />
            </v-col>

            <v-col cols="6">
              <time-select
                v-model="timeClockEndTime"
                :label="$t('v_timeclockdetail.t_end_time')"
                :rules="[(v) => !!v || $t('t_val_req_time')]"
                :times="timeOptions"
                :match-value="timeClockStartTime"
                :original-datetime="timeClock?.clockEnd"
                @update:datetime="updateEndDateTime"
                @input="
                  timeInputsTouched.endTime = true;
                  changeTime();
                "
              />
            </v-col>
          </v-row>
        </div>
        <!-- end replace with new time selector -->
        <!-- /DateTime edit -->
        <div v-if="timeClock">
          <v-row>
            <v-col class="pt-6 mt-1 mb-3">
              <div v-if="timeClock.status == 'a'">
                <v-icon color="success">mdi-eye-check</v-icon>
                <span class="px-2 heading">
                  {{ $t("v_timeclockdetail.t_approved") }}
                  {{ formatDateTime(timeClock.reviewedOn) }}
                </span>
              </div>
              <div v-else-if="timeClock.status == 'i'">
                <v-icon color="warning">mdi-eye-remove</v-icon>
                <span class="px-2 heading">
                  {{ $t("v_timeclockdetail.t_denied") }}
                  {{ formatDateTime(timeClock.reviewedOn) }} {{ $t("v_timeclockdetail.t_by") }}
                  {{ timeClock.reviewedByFriendlyName }}
                </span>
              </div>
              <div v-else>
                <v-icon color="grey lighten-1">mdi-eye-off</v-icon
                ><span class="px-2 heading"> {{ $t("v_timeclockdetail.t_pending_approval") }} </span>
              </div>
            </v-col>
          </v-row>
        </div>
        <v-select
          v-if="(timeClock && timeClock.id != null && timeClock.jobId) || (timeClock && !timeClock.id && jobs?.length)"
          :disabled="timeClock.id != null"
          v-model="timeClock.jobId"
          :items="jobs"
          item-text="jobName"
          item-value="jobId"
          :rules="[(v) => v != null || $t('c_clockin.t_val_req_job')]"
          required
          @input="getCostCodes"
        >
          <v-icon slot="prepend"> mdi-map-marker </v-icon>
          <template #label>
            {{ $t("v_timeclockdetail.t_job_worksite") }}
            <span class="required"></span>
          </template>
        </v-select>
        <v-select
          v-if="timeClock && timeCategories?.length"
          v-model="timeClock.timeCategoryId"
          :items="timeCategories"
          item-text="name"
          item-value="id"
          :rules="[(v) => v != null || $t('c_clockin.t_val_req_category')]"
          required
        >
          <v-icon slot="prepend"> mdi-account-hard-hat </v-icon>
          <template #label>
            {{ $t("v_timeclockdetail.t_category") }}
          </template>
        </v-select>
        <v-select
          v-if="timeClock && costCodes?.length"
          v-model="timeClock.costCodeId"
          :items="costCodes"
          item-value="costCodeId"
          :rules="[(v) => v != null || $t('c_clockin.t_val_req_costcode')]"
          required
          :no-data-text="$t('c_clockin.t_timeclock_no_codes_for_this_job')"
        >
          <template slot="selection" slot-scope="data">
            {{ data.item.costCodeCode }} : {{ data.item.costCodeDescription }}
          </template>
          <template slot="item" slot-scope="data">
            {{ data.item.costCodeCode }} : {{ data.item.costCodeDescription }}
          </template>
          <v-icon slot="prepend"> mdi-currency-usd </v-icon>
          <template #label>
            {{ $t("v_timeclockdetail.t_cost_code") }}
          </template>
        </v-select>
        <v-list two-line class="pt-0" v-if="fitForDutyCheck">
          <v-list-item>
            <v-list-item-action>
              <v-checkbox
                v-model="clockInNote.fitForDuty"
                :rules="[(v) => !!v || $t('t_val_req_fit_for_duty')]"
                required
                :disabled="fitForDutyCheck"
              ></v-checkbox>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $t("v_timeclockdetail.t_fit_for_duty") }}</v-list-item-title>
              <v-list-item-subtitle v-if="timeClock.clockStart && timeClock.clockEnd">
                {{ formatDateTime(timeClock.clockStart) }}</v-list-item-subtitle
              >
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-divider></v-divider>

        <v-list class="mt-3" two-line v-if="timeClock && timeClock.notes">
          <div v-for="(note, index) in timeClock.notes" :key="index + '-check'">
            <v-divider :key="note.id" v-if="index > 0"></v-divider>
            <div>
              <div class="ma-0 px-3 text-right" v-if="!timeClock.id">
                <v-checkbox
                  class="d-inline-block ma-0 pa-0"
                  v-model="timeClock.notes[index].incidentReport"
                  :label="$t('v_timeclockdetail.t_witnessed_a_safety_incident')"
                  color="warning"
                  hide-details
                ></v-checkbox>
              </div>
              <v-list-item :key="index" class="ml-0 pl-1 pr-0" v-if="!note.id || note.note">
                <v-list-item-avatar>
                  <AppUserAvatar :user="getNoteCreator(note)"></AppUserAvatar>
                </v-list-item-avatar>
                <v-list-item-content class="pb-1">
                  <v-list-item-subtitle v-if="note.id"
                    >{{ formatDateTime(note.createdOn) }}
                    <span v-if="note.parentTimeClockNoteId"
                      ><small>{{ $t("v_timeclockdetail.t_[edited]") }}</small></span
                    >
                    <span v-if="note.incidentReport" class="float-right orange--text text--darken-2"
                      ><small>{{ $t("v_timeclockdetail.t_incident_reported") }}</small></span
                    >
                  </v-list-item-subtitle>
                  <v-list-item-title v-if="note.createdById == user.id">
                    <v-textarea
                      name="owned-note"
                      filled
                      auto-grow
                      :label="
                        note.fitForDuty
                          ? 'Clock-in note'
                          : note.incidentReport
                          ? $t('v_timeclockdetail.t_incident_details')
                          : note.note
                          ? $t('v_timeclockdetail.t_note')
                          : $t('v_timeclockdetail.t_add_note')
                      "
                      value=""
                      rows="1"
                      row-height="15"
                      :dense="true"
                      v-model="timeClock.notes[index].note"
                      :rules="
                        timeClock.notes[index].incidentReport === true
                          ? [(v) => !!v || $t('t_val_req_provide_incident_details')]
                          : []
                      "
                    >
                    </v-textarea>
                  </v-list-item-title>

                  <v-list-item-title v-if="note.createdById != user.id">
                    {{ note.note }}
                    <div v-if="note.createdByFriendlyName" class="text-caption grey--text mt-1">
                      {{ note.createdByFriendlyName }}
                    </div>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action
                  class="mx-0 ml-2"
                  v-if="note.createdById == user.id && note.id && timeClock.status == 'p'"
                >
                  <v-btn icon @click="timeClock.notes[index].note = ''"
                    ><v-icon color="grey lighten-1">mdi-delete</v-icon></v-btn
                  >
                </v-list-item-action>
              </v-list-item>
            </div>
          </div>
        </v-list>
        <div v-if="!$online && timeClock && timeClock.clockStartNote">
          <span class="caption">{{ $t("v_timeclockdetail.t_clockin_note") }}</span
          ><br />{{ timeClock.clockStartNote }}
        </div>
        <div v-if="!$online && timeClock && timeClock.clockEndNote">
          <span class="caption">{{ $t("v_timeclockdetail.t_t_clockout_note") }}</span
          ><br />{{ timeClock.clockEndNote }}
        </div>
        <div v-if="timeClock" :class="timeClock.notes && timeClock.notes.length ? 'mt-0' : 'mt-5'"></div>
        <v-card-actions v-if="timeClock">
          <v-btn :to="{ name: 'timeclock' }" small> {{ $t("t_cancel") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="$online"
            :disabled="timeClock.status == 'a' || timeClock.status == 'i' || !isValid"
            color="primary"
            class="mr-4 px-9"
            @click="validateAndSubmit"
          >
            {{ timeClock.id ? $t("v_timeclockdetail.t_update") : $t("v_timeclockdetail.t_submit") }}
          </v-btn>
          <span v-else-if="!timeClock.reviewedOn">{{
            $t("v_timeclockdetail.t_updates_require_a_network_connection")
          }}</span>
          <span class="success--text" v-if="timeClock.status == 'a'">{{
            $t("v_timeclockdetail.t_time_entry_approved")
          }}</span>
          <span class="warning--text" v-if="timeClock.status == 'i'">{{
            $t("v_timeclockdetail.t_time_entry_denied")
          }}</span>
        </v-card-actions>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Helpers from "@/mixins/helpers";
import { DateTime } from "luxon";
import AppUserAvatar from "@/components/AppUserAvatar.vue";
import TimeSelect from "@/components/TimeSelect.vue";

export default {
  name: "TimeClockDetail",
  metaInfo() {
    return {
      title: this.timeClockStartDate ? `${this.timeClockStartDate}` : "Detail",
    };
  },
  components: {
    AppUserAvatar,
    TimeSelect,
  },
  model: {},
  mixins: [Helpers],
  computed: {
    ...mapGetters("timeClock", ["timeClockDuration"]),
    ...mapGetters("user", ["user"]),
    ...mapGetters("settings", ["settings"]),
    fitForDutyCheck() {
      if (this.timeClock?.notes) {
        return this.timeClock.notes.some((val) => val.fitForDuty);
      }
      return false;
    },
    clockInNote() {
      if (this.fitForDutyCheck) {
        return this.timeClock.notes.find((val) => val.fitForDuty);
      }
      return false;
    },
    locale() {
      return this.$store.state?.settings?.lang;
    },
    formattedStartDate() {
      if (!this.timeClockStartDate) return "";
      return DateTime.fromFormat(this.timeClockStartDate, "yyyy-MM-dd").toFormat("MMMM d, yyyy");
    },
    formattedEndDate() {
      if (!this.timeClockEndDate || this.timeClockEndDate === this.timeClockStartDate) {
        return DateTime.fromISO(this.timeClockStartDate).toFormat("MMMM d, yyyy");
      }
      return DateTime.fromISO(this.timeClockEndDate).toFormat("MMMM d, yyyy");
    },
    formatTimeValue() {
      return (timeValue) => {
        if (!timeValue) return "";
        try {
          const today = DateTime.now().toFormat("yyyy-MM-dd");
          const fullDateTime = `${today}T${timeValue}`;
          const time = DateTime.fromISO(fullDateTime, { zone: this.settings?.timeZone || "local" });
          return this.settings.timeFormat24Hour ? time.toFormat("HH:mm:ss") : time.toFormat("hh:mm a");
        } catch (error) {
          console.error("Error formatting time:", error);
          return timeValue;
        }
      };
    },
    timeClockStartTimeFormatted() {
      return this.formatTimeValue(this.timeClockStartTime);
    },
    timeClockEndTimeFormatted() {
      return this.formatTimeValue(this.timeClockEndTime);
    },
    timeOptions() {
      const times = [];
      // Start from midnight (00:00)
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const time = DateTime.fromObject({ hour, minute }, { zone: this.settings?.timeZone || "local" });
          const formattedTime = this.settings.timeFormat24Hour ? time.toFormat("HH:mm:ss") : time.toFormat("h:mm a");
          times.push(formattedTime);
        }
      }
      return times;
    },
    sortedTimeOptions() {
      return (selectedTime) => {
        if (!selectedTime) return this.timeOptions;

        // Find the selected time index
        const selectedIndex = this.timeOptions.findIndex((time) => time === selectedTime);
        if (selectedIndex === -1) return this.timeOptions;

        // Get times before and after the selected time
        const before = this.timeOptions.slice(0, selectedIndex);
        const after = this.timeOptions.slice(selectedIndex + 1);

        // Reorder the array to show selected time and nearby times first
        const reordered = [
          selectedTime,
          ...after.slice(0, 4), // Next 4 times
          ...before.slice(-4), // Previous 4 times
          ...after.slice(4), // Rest of later times
          ...before.slice(0, -4), // Rest of earlier times
        ];

        return reordered;
      };
    },
    effectiveEndDate() {
      // If no end date or same as start date, return start date
      return this.timeClockEndDate && this.timeClockEndDate !== this.timeClockStartDate
        ? this.timeClockEndDate
        : this.timeClockStartDate;
    },
  },
  data() {
    return {
      timeClock: null,
      timeclockNotesOrig: null,
      showTimeEdit: false,
      showEndDate: false,
      menuStartDate: false,
      menuEndDate: false,
      timeClockStartDate: DateTime.now().toISODate(),
      timeClockEndDate: null,
      timeClockStartTime: "08:00:00",
      timeClockEndTime: "09:00:00",
      timeClockNewDuration: "1:00",
      timeClockId: null,
      jobs: [],
      timeCategories: [],
      costCodes: [],
      isValid: false,
      useLastClock: false,
      timeInputsTouched: { duration: false, startTime: false, endTime: false },
    };
  },
  methods: {
    ...mapActions("timeClock", [
      "getTimeClock",
      "getTimeClockLatest",
      "saveTimeClock",
      "saveTimeClockNote",
      "getUserJobs",
      "getGroupJobs",
      "getCategories",
      "getJobCostCodes",
      "resetTimeClockState",
    ]),

    getNoteCreator(note) {
      // If createdBy object exists, ensure it has userId for AppUserAvatar
      if (note.createdBy) {
        return {
          ...note.createdBy,
          userId: note.createdBy.id || note.createdBy.userId, // Ensure userId is set for AppUserAvatar
        };
      }

      // If note has an id and was created by someone else, construct user object from note properties
      if (note.id && note.createdById && note.createdById !== this.user.id) {
        return {
          id: note.createdById,
          userId: note.createdById,
          friendlyName: note.createdByFriendlyName,
          profileImageAssetId: note.createdByProfileImageAssetId,
          profileImageAssetKey: note.createdByProfileImageAssetKey,
          initials: note.createdByInitials,
          hasProfileImageAsset: !!note.createdByProfileImageAssetId,
        };
      }

      // Default to current user (for new notes being created)
      return this.user;
    },

    initializeDefaultTimes() {
      if (!this.timeClockId) {
        const now = DateTime.now().setZone(this.settings?.timeZone || "local");
        const currentHour = now.hour;

        let startTime;
        if (currentHour < 8) {
          startTime = now.set({ hour: 8, minute: 0, second: 0 });
        } else if (currentHour >= 17) {
          startTime = now.plus({ days: 1 }).set({ hour: 8, minute: 0, second: 0 });
        } else {
          startTime = now.set({ minute: 0, second: 0 });
        }

        // Set end time to 4 hours after start time
        const endTime = startTime.plus({ hours: 4 });

        this.timeClockStartDate = startTime.toISODate();
        this.timeClockEndDate = endTime.toISODate();
        this.timeClockStartTime = this.settings.timeFormat24Hour
          ? startTime.toFormat("HH:mm:ss")
          : startTime.toFormat("hh:mm a");
        this.timeClockEndTime = this.settings.timeFormat24Hour
          ? endTime.toFormat("HH:mm:ss")
          : endTime.toFormat("hh:mm a");
        this.timeClockNewDuration = "4:00";

        // Set the initial clock times
        if (this.timeClock) {
          this.timeClock.clockStart = startTime.toUTC().toISO();
          this.timeClock.clockEnd = endTime.toUTC().toISO();
        }
      }
    },

    changeTime() {
      const startTime = this.parseTimeInput(this.timeClockStartTime, true);
      const endTime = this.parseTimeInput(this.timeClockEndTime);
      if (!startTime || !endTime) return;

      // Create DateTime objects with the correct dates and timezone
      let startDate = DateTime.fromISO(this.timeClockStartDate, {
        zone: this.settings?.timeZone || "local",
      }).set({
        hour: startTime.hour,
        minute: startTime.minute,
        second: 0,
      });

      // Create end datetime using the effective end date
      let endDate = DateTime.fromISO(this.effectiveEndDate, {
        zone: this.settings?.timeZone || "local",
      }).set({
        hour: endTime.hour,
        minute: endTime.minute,
        second: 0,
      });

      // If no end date is shown (single day view) and end time is before start time
      if ((!this.timeClockEndDate || this.timeClockEndDate === this.timeClockStartDate) && endDate < startDate) {
        endDate = endDate.set({
          year: startDate.year,
          month: startDate.month,
          day: startDate.day,
        });
      }

      if (this.timeClock) {
        this.timeClock.clockStart = startDate.toUTC().toISO();
        this.timeClock.clockEnd = endDate.toUTC().toISO();
        this.updateDuration();
      }
    },

    parseTimeInput(timeStr, isStartTime = false) {
      if (!timeStr) return null;

      try {
        let time;
        if (this.settings.timeFormat24Hour) {
          const [hours, minutes] = timeStr.split(":");
          time = DateTime.fromObject(
            { hour: parseInt(hours), minute: parseInt(minutes), second: 0 },
            { zone: this.settings?.timeZone || "local" },
          );
        } else {
          // Handle various input formats
          let hour, minute, meridiem;

          // If input is just numbers (like "755" for 7:55)
          if (/^\d{3,4}$/.test(timeStr)) {
            const digits = timeStr.padStart(4, "0");
            hour = parseInt(digits.slice(0, 2));
            minute = parseInt(digits.slice(2));

            // For start time, compare with end time to determine AM/PM
            if (isStartTime) {
              // Default to AM for morning hours (5-11)
              if (hour >= 5 && hour <= 11) {
                meridiem = "AM";
              } else if (this.timeClockEndTime) {
                const endTime = this.parseTimeInput(this.timeClockEndTime);
                if (endTime) {
                  // Convert both times to minutes since midnight for comparison
                  const endMinutes = endTime.hour * 60 + endTime.minute;
                  const startMinutesAM = hour * 60 + minute;

                  // Choose AM if it creates a valid duration and is before end time
                  if (startMinutesAM < endMinutes) {
                    meridiem = "AM";
                  } else {
                    meridiem = "PM";
                  }
                }
              } else {
                // No end time to compare against, use reasonable defaults
                meridiem = hour >= 1 && hour <= 11 ? "AM" : "PM";
              }
            } else {
              // For end time, use standard business hour logic
              meridiem = hour >= 1 && hour <= 11 ? "AM" : "PM";
              // Special case: 12 should be PM
              if (hour === 12) meridiem = "PM";
            }
          } else {
            // Handle standard time format (e.g., "7:55 AM")
            const parts = timeStr.split(/[\s:]+/);
            if (parts.length >= 2) {
              hour = parseInt(parts[0]);
              minute = parseInt(parts[1]);
              meridiem = parts[2] || "AM"; // Default to AM if not specified
            } else {
              return null;
            }
          }

          // Adjust hour based on meridiem
          if (meridiem === "PM" && hour !== 12) hour += 12;
          if (meridiem === "AM" && hour === 12) hour = 0;

          time = DateTime.fromObject({ hour, minute, second: 0 }, { zone: this.settings?.timeZone || "local" });
        }
        return time;
      } catch (error) {
        console.error("Error parsing time:", error);
        return null;
      }
    },

    changeTimeClockDuration(val) {
      if (!val) return;

      try {
        let hours = 0,
          minutes = 0;

        // Handle special case where duration is just minutes (less than 1 hour)
        if (!val.includes(":") && !val.includes(".") && parseInt(val) < 60) {
          minutes = parseInt(val);
        } else if (val.includes(":")) {
          [hours, minutes] = val.split(":").map((num) => parseInt(num, 10));
        } else if (val.includes(".")) {
          const decimal = parseFloat(val);
          hours = Math.floor(decimal);
          minutes = Math.round((decimal - hours) * 60);
        } else {
          hours = parseInt(val, 10);
        }

        if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes >= 60) return;

        const startTime = this.parseTimeInput(this.timeClockStartTime, true);
        if (!startTime) return;

        const start = DateTime.fromISO(this.timeClockStartDate, { zone: this.settings?.timeZone || "local" }).set({
          hour: startTime.hour,
          minute: startTime.minute,
          second: 0,
        });
        const end = start.plus({ hours, minutes });

        // Only set timeClockEndDate if it's different from start date
        this.timeClockEndDate = end.toISODate() !== this.timeClockStartDate ? end.toISODate() : null;

        this.timeClockEndTime = this.settings.timeFormat24Hour ? end.toFormat("HH:mm:ss") : end.toFormat("h:mm a");

        if (this.timeClock) {
          this.timeClock.clockEnd = end.toUTC().toISO();
          this.updateDuration();
        }

        this.timeClockNewDuration = `${hours}:${minutes.toString().padStart(2, "0")}`;
      } catch (error) {
        console.error("Error updating duration:", error);
      }
    },

    addEmptyNote() {
      let note = {
        createdById: this.user ? this.user.id : null,
        createdOn: DateTime.utc().toISO(),
        fitForDuty: false,
        note: "",
        timeClockId: this.timeClockId,
      };
      if (this.timeClock && this.timeClock.notes?.length > 0) {
        this.timeClock.notes.unshift(note);
      } else {
        this.$set(this.timeClock, "notes", [note]);
      }
    },

    validateAndSubmit() {
      this.isValid = this.$refs.formTimeClock.validate();
      if (this.isValid) {
        this.submitTimeClock();
      } else {
        this.$snackbar.showMessage({
          content: this.$t("t_something_went_wrong"),
          color: "warning",
          timeout: "",
        });
      }
    },

    fillLastClock() {
      if (this.useLastClock) {
        this.getTimeClockLatest().then((lastClock) => {
          const skipTime = Object.values(this.timeInputsTouched).includes(true);
          if (!skipTime) {
            if (lastClock.clockEnd) {
              this.timeClockStartTime = this.formatTime24(lastClock.clockStart);
              this.timeClockEndTime = this.formatTime24(lastClock.clockEnd);
            }
            this.changeTime();
          }
          this.timeClock.timeCategoryId = lastClock.timeCategoryId;
          this.timeClock.costCodeId = lastClock.costCodeId;
          this.timeClock.jobId = lastClock.jobId;
          this.getCostCodes(lastClock.jobId);
        });
      }
    },

    async submitTimeClock() {
      const clockEdit = {
        id: this.timeClock.id,
        timeCategoryId: this.timeClock.timeCategoryId,
        costCodeId: this.timeClock.costCodeId,
        clockStartReasonId: this.timeClock.clockStartReasonId,
        clockStartLatLng: this.timeClock.clockStartLatLng,
        clockEndLatLng: this.timeClock.clockEndLatLng,
        clockStartLatLngMethod: this.getDeviceInfo(),
        clockEndLatLngMethod: this.getDeviceInfo(),
      };

      if (this.showTimeEdit) {
        // Create DateTime objects with the correct dates and times
        const startTime = this.parseTimeInput(this.timeClockStartTime, true);
        const endTime = this.parseTimeInput(this.timeClockEndTime);

        const startDate = DateTime.fromISO(this.timeClockStartDate, { zone: this.settings?.timeZone || "local" }).set({
          hour: startTime.hour,
          minute: startTime.minute,
          second: 0,
        });

        const endDate = DateTime.fromISO(this.effectiveEndDate, { zone: this.settings?.timeZone || "local" }).set({
          hour: endTime.hour,
          minute: endTime.minute,
          second: 0,
        });

        clockEdit.clockStart = startDate.toUTC().toISO();
        clockEdit.clockEnd = endDate.toUTC().toISO();
      } else {
        clockEdit.clockStart = this.timeClock.clockStart;
        clockEdit.clockEnd = this.timeClock.clockEnd;
      }

      if (!this.timeClock.id) {
        clockEdit.jobId = this.timeClock.jobId;
      }

      // Save time clock and notes
      const saveClock = [];
      if (this.timeClock.id) {
        this.timeClock.notes.forEach((note) => {
          // Skip empty notes (both new placeholders and existing notes with no content)
          if (!note.note || !note.note.trim()) {
            return;
          }

          const isNew = !note.id;
          const isModified = note.id && this.timeclockNotesOrig.find((val) => val.id === note.id)?.note !== note.note;

          if (isNew || isModified) {
            this.saveNote(note, saveClock);
          }
        });
      }
      saveClock.push(this.saveTimeClock(clockEdit));

      try {
        const results = await Promise.all(saveClock);

        // Handle new time clock note if exists
        if (!this.timeClock.id && results[0]?.data.timeclockId && this.timeClock.notes[0]?.note) {
          const note = this.timeClock.notes[0];
          note.timeClockId = results[0].data.timeclockId;
          await this.saveNote(note, []);
        }

        // Update the original notes to prevent re-saving on next update
        if (this.timeClock.id) {
          this.timeclockNotesOrig = JSON.parse(JSON.stringify(this.timeClock.notes));
        }

        this.responseMessage("c_snackbar.t_success_timeclock_saved", 2000);
        this.$router.push({ name: "timeclock", params: { fetchTimeClocks: true } });
      } catch (error) {
        console.error("[TimeClockDetail] Error saving timeclock:", error);
        // Don't navigate away if there's an error
        throw error;
      }
    },

    saveNote(note, saveClock) {
      let noteEdit = {};
      if (note.id) {
        noteEdit.id = note.id;
      }
      noteEdit.timeClockId = note.timeClockId ? note.timeClockId : this.timeClock.id;
      noteEdit.note = note.note;
      noteEdit.incidentReport = note.incidentReport;
      noteEdit.fitForDuty = note.fitForDuty;
      return saveClock.push(this.saveTimeClockNote(noteEdit));
    },

    formTimeClockReset() {
      this.$refs.formTimeClock.reset();
    },

    resetClockIn() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },

    getCostCodes() {
      this.costCodes = [];
      let params = {};
      params.jobId = this.timeClock.jobId;
      this.getJobCostCodes(params).then((response) => {
        this.costCodes = response;
      });
    },

    toggleEndDate() {
      this.showEndDate = !this.showEndDate;
      if (this.showEndDate) {
        // Parse current duration
        const [hours, minutes] = this.timeClockNewDuration.split(":").map((num) => parseInt(num));
        if (isNaN(hours) || isNaN(minutes)) return;

        // Add 24 hours to current duration
        const totalHours = hours + 24;

        // Get current start time
        const startTime = this.parseTimeInput(this.timeClockStartTime, true);
        if (!startTime) return;

        // Create start datetime
        const startDateTime = DateTime.fromISO(this.timeClockStartDate, {
          zone: this.settings?.timeZone || "local",
        }).set({
          hour: startTime.hour,
          minute: startTime.minute,
          second: 0,
        });

        // Calculate end time based on total duration
        const endDateTime = startDateTime.plus({ hours: totalHours, minutes });

        // Only set timeClockEndDate if it's different from start date
        this.timeClockEndDate = endDateTime.toISODate() !== this.timeClockStartDate ? endDateTime.toISODate() : null;

        // Update the end time
        this.timeClockEndTime = this.settings.timeFormat24Hour
          ? endDateTime.toFormat("HH:mm:ss")
          : endDateTime.toFormat("h:mm a");

        // Update timeClock and duration
        if (this.timeClock) {
          this.timeClock.clockEnd = endDateTime.toUTC().toISO();
          this.timeClockNewDuration = `${totalHours}:${minutes.toString().padStart(2, "0")}`;
        }
      } else {
        // When hiding end date, reset to null and recalculate based on current times
        this.timeClockEndDate = null;
        this.changeTime();
      }
    },

    resetEndDate() {
      if (!this.timeClockStartDate) return;

      this.showEndDate = false;
      this.timeClockEndDate = null;

      // Get current times
      const startTime = this.parseTimeInput(this.timeClockStartTime, true);
      const endTime = this.parseTimeInput(this.timeClockEndTime);
      if (!startTime || !endTime) return;

      // Create start datetime
      const startDateTime = DateTime.fromISO(this.timeClockStartDate, { zone: this.settings?.timeZone || "local" }).set(
        {
          hour: startTime.hour,
          minute: startTime.minute,
          second: 0,
        },
      );

      // Create end datetime on same day
      let endDateTime = DateTime.fromISO(this.timeClockStartDate, { zone: this.settings?.timeZone || "local" }).set({
        hour: endTime.hour,
        minute: endTime.minute,
        second: 0,
      });

      // If end time is earlier than start time, add a day but keep timeClockEndDate null
      if (endDateTime < startDateTime) {
        endDateTime = endDateTime.plus({ days: 1 });
      }

      // Update end time
      this.timeClockEndTime = this.settings.timeFormat24Hour
        ? endDateTime.toFormat("HH:mm:ss")
        : endDateTime.toFormat("h:mm a");

      if (this.timeClock) {
        this.timeClock.clockEnd = endDateTime.toUTC().toISO();
      }

      // Update duration
      this.updateDuration();
    },

    updateStartDateTime(utcTime) {
      if (this.timeClock) {
        this.timeClock.clockStart = utcTime;
        const localTime = DateTime.fromISO(utcTime, { zone: "utc" }).setZone(this.settings?.timeZone || "local");
        this.timeClockStartTime = this.settings.timeFormat24Hour
          ? localTime.toFormat("HH:mm:ss")
          : localTime.toFormat("h:mm a");

        // Update duration immediately
        this.updateDuration();
      }
    },

    updateEndDateTime(utcTime) {
      if (this.timeClock) {
        this.timeClock.clockEnd = utcTime;
        const localTime = DateTime.fromISO(utcTime, { zone: "utc" }).setZone(this.settings?.timeZone || "local");
        this.timeClockEndTime = this.settings.timeFormat24Hour
          ? localTime.toFormat("HH:mm:ss")
          : localTime.toFormat("h:mm a");

        // Update duration immediately
        this.updateDuration();
      }
    },

    updateDuration() {
      if (!this.timeClock?.clockStart || !this.timeClock?.clockEnd) return;

      try {
        const start = DateTime.fromISO(this.timeClock.clockStart, { zone: "utc" }).setZone(
          this.settings?.timeZone || "local",
        );
        const end = DateTime.fromISO(this.timeClock.clockEnd, { zone: "utc" }).setZone(
          this.settings?.timeZone || "local",
        );

        if (!start.isValid || !end.isValid) return;

        let totalMinutes;

        // If no end date is shown (single day view)
        if (!this.timeClockEndDate || this.timeClockEndDate === this.timeClockStartDate) {
          // Calculate minutes within the same day
          // If end time is before start time, we want the actual difference
          // rather than adding 24 hours
          totalMinutes = end.diff(start, "minutes").minutes;
          if (totalMinutes < 0) {
            // For times like 7:55 AM to 8:20 AM, we want 25 minutes
            totalMinutes = end.hour * 60 + end.minute - (start.hour * 60 + start.minute);
          }
        } else {
          // For multi-day entries, calculate the full duration
          totalMinutes = Math.round(end.diff(start, "minutes").minutes);
        }

        if (totalMinutes < 0) {
          this.timeClockNewDuration = "0:00";
          return;
        }

        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.round(totalMinutes % 60);
        this.timeClockNewDuration = `${hours}:${minutes.toString().padStart(2, "0")}`;
      } catch (error) {
        console.error("Error calculating duration:", error);
        this.timeClockNewDuration = "0:00";
      }
    },
  },
  watch: {
    timeClockStartTime: {
      handler(newVal) {
        if (newVal) {
          this.changeTime();
        }
      },
      immediate: true,
    },
    timeClockEndTime: {
      handler(newVal) {
        if (newVal) {
          this.changeTime();
        }
      },
      immediate: true,
    },
    timeClockEndDate(newVal) {
      if (!newVal || !this.timeClockStartDate) return;

      // If end date is same as or before start date
      if (DateTime.fromISO(newVal) <= DateTime.fromISO(this.timeClockStartDate)) {
        this.resetEndDate();
      }
    },
    timeClockStartDate(newVal) {
      if (!newVal) return;

      // Get current start and end times
      const startTime = this.parseTimeInput(this.timeClockStartTime, true);
      if (!startTime) return;

      // Parse current duration
      const [hours, minutes] = this.timeClockNewDuration.split(":").map((num) => parseInt(num));
      if (isNaN(hours) || isNaN(minutes)) return;

      // Create new start datetime
      const newStartDateTime = DateTime.fromISO(newVal, { zone: this.settings?.timeZone || "local" }).set({
        hour: startTime.hour,
        minute: startTime.minute,
        second: 0,
      });

      // Calculate new end time based on duration
      const newEndDateTime = newStartDateTime.plus({ hours, minutes });

      // Only set timeClockEndDate if it's different from start date
      this.timeClockEndDate = newEndDateTime.toISODate() !== newVal ? newEndDateTime.toISODate() : null;

      this.timeClockEndTime = this.settings.timeFormat24Hour
        ? newEndDateTime.toFormat("HH:mm:ss")
        : newEndDateTime.toFormat("h:mm a");

      // Update timeClock object if it exists
      if (this.timeClock) {
        this.timeClock.clockStart = newStartDateTime.toUTC().toISO();
        this.timeClock.clockEnd = newEndDateTime.toUTC().toISO();
      }

      // Prevent duration from being recalculated
      this.timeInputsTouched.duration = true;
    },
  },
  mounted() {
    let params = this.$route.params;
    this.timeClockId = params.timeClockId;
    if (this.$online) {
      this.isValid = false;
    }

    if (this.timeClockId) {
      this.timeClock = null;
      this.getTimeClock(this.timeClockId).then((r) => {
        this.timeClock = r;
        this.getCostCodes();
        if (this.timeClock && this.timeClock.clockEnd) {
          const startLocal = DateTime.fromISO(this.timeClock.clockStart, { zone: "utc" }).setZone(
            this.settings?.timeZone || "local",
          );
          const endLocal = DateTime.fromISO(this.timeClock.clockEnd, { zone: "utc" }).setZone(
            this.settings?.timeZone || "local",
          );

          this.timeClockStartDate = startLocal.toISODate();
          this.timeClockEndDate = endLocal.toISODate();
          this.timeClockStartTime = this.settings.timeFormat24Hour
            ? startLocal.toFormat("HH:mm:ss")
            : startLocal.toFormat("hh:mm a");
          this.timeClockEndTime = this.settings.timeFormat24Hour
            ? endLocal.toFormat("HH:mm:ss")
            : endLocal.toFormat("hh:mm a");
          this.showTimeEdit = true;
        } else {
          this.showTimeEdit = false;
        }
        this.timeClockNewDuration = this.formatDuration(this.timeClock.clockStart, this.timeClock.clockEnd);
        this.timeClock.notes?.sort((a, b) => b.id - a.id);
        this.timeclockNotesOrig = JSON.parse(JSON.stringify(this.timeClock.notes));
        this.addEmptyNote();
      });
    } else {
      this.resetTimeClockState();
      this.timeClock = this.$store.state.timeClock.timeClock;
      this.$refs.formTimeClock.resetValidation();
      this.showTimeEdit = true;
      this.timeClock.userId = this.user.id;
      this.timeClock.companyId = this.user.companyId;
      this.timeClock.createdById = this.user.id;

      const startTimeMST = DateTime.fromObject(
        { hour: 8, minute: 0, second: 0 },
        { zone: "America/Denver" }, // MST timezone
      );
      const endTimeMST = startTimeMST.plus({ hours: 4 });

      // Convert to user's timezone
      const startTime = startTimeMST.setZone(this.settings?.timeZone || "local");
      const endTime = endTimeMST.setZone(this.settings?.timeZone || "local");

      this.timeClockStartDate = startTime.toISODate();
      // Only set timeClockEndDate if it's different from start date
      this.timeClockEndDate = endTime.toISODate() !== startTime.toISODate() ? endTime.toISODate() : null;

      this.timeClockStartTime = this.settings.timeFormat24Hour
        ? startTime.toFormat("HH:mm:ss")
        : startTime.toFormat("hh:mm a");
      this.timeClockEndTime = this.settings.timeFormat24Hour
        ? endTime.toFormat("HH:mm:ss")
        : endTime.toFormat("hh:mm a");
      this.timeClockNewDuration = "4:00";

      // Set the timeClock object times in UTC
      this.timeClock.clockStart = startTime.toUTC().toISO();
      this.timeClock.clockEnd = endTime.toUTC().toISO();

      this.createdOn = DateTime.utc();
      this.addEmptyNote();
    }
    const jobParams = { userId: this.$store.state.user.user.userId };
    Promise.all([this.getUserJobs(jobParams), this.getGroupJobs(jobParams)]).then(([userJobs, groupJobs]) => {
      this.jobs = [...(userJobs || []), ...(groupJobs || [])];
      this.jobs = this.jobs.filter((job, index, self) => index === self.findIndex((j) => j.jobId === job.jobId));
    });
    this.getCategories().then((response) => {
      this.timeCategories = response;
    });
  },
};
</script>
<style lang="scss">
.clockin-dialog {
  bottom: 0;
  top: initial !important;
  border-top: 5px solid #777;
  background: #aaa;
}
.max-width-30 {
  max-width: 30px;
}
.height-50 {
  height: 50px !important;
}
.time-select-menu {
  .v-list-item {
    min-height: 40px !important;
  }

  .v-list {
    max-height: 300px;
    overflow-y: auto;
  }
}

.hide-selected {
  .v-select__selection {
    width: 100%;

    .time-input {
      padding-top: 0 !important;
      margin-top: -8px !important;

      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }

  .v-select__slot {
    cursor: pointer;
  }
}
</style>
